import { requiredIf } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { DaysOfWeek, TypeCampaignEnum } from '@/types';
import { includesValuesValidator, requiredWithMessage } from '@/helpers/CustomValidator';
import type { FlowOperatorMetadata, ValidationResult } from '@/types/flow-types';
import Form from './Form.vue';
import { canDeleteTrigger } from '@/components/flows/operators/registry';

export interface PeriodicLaunchingData {
  time: string;
  multiplebox_trigger_timezone_clients: string;
  multiplebox_trigger_type: string;
  multiplebox_trigger_days: {
    [key: number]: DaysOfWeek;
  };
}

export function createDefaultData(data: Partial<PeriodicLaunchingData> = {}): PeriodicLaunchingData {
  return {
    time: data.time ?? '20:00',
    multiplebox_trigger_timezone_clients: data.multiplebox_trigger_timezone_clients ?? '1',
    multiplebox_trigger_type: data.multiplebox_trigger_type ?? '0',
    multiplebox_trigger_days: data.multiplebox_trigger_days ?? {},
  };
}

const PeriodicLaunching: FlowOperatorMetadata<PeriodicLaunchingData> = {
  id: 'boxperiodiclaunching',
  kind: 'trigger',
  label: 'automatedScenarios.triggers.periodicLaunching.label',
  icon: 'fa-calendar',
  hasConfiguration: true,
  availableIn: [TypeCampaignEnum.AUTOMATION],

  incompatibilities: {
    [TypeCampaignEnum.AUTOMATION]: {
      nextOperators: [
        'boxabandonpanier', 'boxsitepasstime', 'boxquitsite',
        'boxperiodiclaunching', 'boxscrollpage', 'boxstatuscommand',
        'boxcustomjs', 'boxregisteronsite', 'boxsubscribetonewsletter',
        'boxtag', 'boxlist', 'boxsendemail',
        'boxsendsms', 'boxabtest', 'boxdisplayfreepopup',
        'boxdisplayfreemodel', 'boxsendfbmessenger', 'boxsendpushnotification',
        'openemail', 'clicklink', 'boxvisitpage',
        'boxdelaismax', 'boxnavigationtrigger', 'boxwait',
        'boxtriggerinternalevent', 'boxsendrcs', 'boxsendwhatsapp',
      ],
    },
  },

  Form,

  defaultData: createDefaultData,

  outputs: () => 1,

  async validate(data: PeriodicLaunchingData): Promise<ValidationResult> {
    const rules = {
      time: {
        requiredWithMessage: requiredWithMessage(),
      },
      multiplebox_trigger_timezone_clients: {
        requiredWithMessage: requiredWithMessage(),
        includesValuesValidator: includesValuesValidator(['0', '1']),
      },
      multiplebox_trigger_type: {
        requiredWithMessage: requiredWithMessage(),
        includesValuesValidator: includesValuesValidator(['0', '1']),
      },
      multiplebox_trigger_days: {
        required: requiredIf(() => data.multiplebox_trigger_type === '1'),
      },
    };

    const v$ = useVuelidate(rules, data);
    const success = await v$.value.$validate();
    return {
      success,
      validate: v$,
    };
  },

  canBeDeleted(operatorId: string, flow: any): Promise<boolean | string> {
    return canDeleteTrigger(flow, operatorId);
  },
};

export default PeriodicLaunching;
